<template>
  <v-dialog v-model="dialogVisible" max-width="1400px" persistent class="column-mapping-dialog">
    <v-card class="d-flex flex-column" style="height: 90vh; max-height: 90vh; overflow-y: overlay">
      <!-- Header -->
      <v-card-title class="d-flex align-center justify-space-between pa-4 bg1">
        <div class="d-flex align-center">
          <v-icon class="mr-3" color="primary">mdi-table-arrow-right</v-icon>
          <div>
            <h3>Map Excel Columns</h3>
            <p class="text-caption mb-0 text--secondary">
              Map your Excel columns to AVA's standard fields
            </p>
          </div>
        </div>
        <v-btn icon @click="closeDialog" :disabled="isProcessing">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- File Info -->
      <v-card-subtitle v-if="uploadData.fileName" class="py-2 px-4 mt-0">
        <v-chip label class="mr-2" small color="primary">
          {{ uploadData.fileName }}
        </v-chip>
        <v-chip class="mr-2" small color="success" outlined> {{ tagType }} Master </v-chip>
      </v-card-subtitle>

      <!-- Facility Column Selection -->
      <v-card-text class="py-2 px-4">
        <v-row>
          <v-col cols="12" md="10">
            <v-select
              v-model="facilityColumn"
              :items="facilityColumnOptions"
              hint="Select the column that contains facility information for automatic filtering."
              label="Facility Column"
              dense
              outlined
              clearable
              hide-details
              @change="onFacilityColumnChange">
              <template v-slot:prepend-inner>
                <v-icon>mdi-domain</v-icon>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" md="2" v-if="facilityFilterStatus">
            <v-chip
              :color="facilityFilterStatus.isActive ? 'success' : 'warning'"
              outlined
              small
              class="mt-2">
              <v-icon left small>
                {{ facilityFilterStatus.isActive ? 'mdi-filter-check' : 'mdi-filter-off' }}
              </v-icon>
              {{ facilityFilterStatus.facilityName }}
            </v-chip>
          </v-col>
        </v-row>
        
        <!-- Facility Filter Status -->
        <v-alert
          v-if="facilityFilterStatus && facilityFilterStatus.isActive"
          outlined
          type="info"
          dense
          class="mt-2"
          border="left">
          <div class="d-flex align-center">
            <div>
              <strong>Facility Filter Active:</strong> Data will be filtered to match "{{ facilityFilterStatus.facilityName }}"
              <div class="text-caption">
                Column "{{ facilityFilterStatus.mappedColumn }}" is mapped to facility name
              </div>
            </div>
          </div>
        </v-alert>
      </v-card-text>

      <!-- Process Data Disabled Alert -->
      <v-card-text v-if="shouldShowDisabledAlert" class="pt-0 pb-2 px-4">
        <v-alert
          v-for="(reason, index) in processDataDisabledReasons"
          :key="index"
          :type="reason.type"
          outlined
          dense
          class="mb-2">
          <div class="d-flex align-start">
            <div class="flex-grow-1">
              <div class="font-weight-medium">{{ reason.message }}</div>
              <div class="text-caption mt-1">{{ reason.solution }}</div>
            </div>
          </div>
        </v-alert>
      </v-card-text>

      <!-- Main Content -->
      <v-card-text class="flex-grow-1 pa-0" style="overflow-y: auto">
        <v-container fluid class="pa-4">
          <v-row style="height: 100%">
            <!-- Excel Columns -->
            <v-col cols="12" md="4" class="d-flex flex-column">
              <h4 class="mb-3 d-flex align-center">
                <v-icon class="mr-2" color="orange">mdi-file-excel</v-icon>
                Excel Columns ({{ filteredExcelColumns.length }})
                <v-chip v-if="facilityFilterStatus && facilityFilterStatus.isActive" x-small color="info" class="ml-2">
                  Filtered
                </v-chip>
              </h4>
              <v-card outlined class="flex-grow-1 overflow-auto">
                <v-list dense class="pt-0">
                  <draggable
                    v-model="excelColumns"
                    group="columns"
                    class="list-group"
                    ghost-class="ghost"
                    @start="onDragStart"
                    @end="onDragEnd">
                    <v-list-item
                      v-for="column in filteredExcelColumns"
                      :key="column.name"
                      class="column-item excel-column"
                      :class="{
                        mapped: isMapped(column.name),
                        dragging: isDragging,
                      }">
                      <v-list-item-avatar size="32">
                        <v-icon :color="isMapped(column.name) ? 'success' : 'grey'" size="16">
                          {{ isMapped(column.name) ? 'mdi-check-circle' : 'mdi-drag' }}
                        </v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2">
                          {{ column.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ getColumnPreview(column.name) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <div v-if="isMapped(column.name)" class="d-flex">
                          <v-btn x-small icon color="error" @click="removeMapping(column.name)">
                            <v-icon size="14">mdi-close</v-icon>
                          </v-btn>
                        </div>
                        <div v-else>
                          <PropertySelector
                            :column-header="column.name"
                            :tag-type="tagType"
                            :existing-mappings="columnMappings"
                            @property-selected="onPropertySelected" />
                        </div>
                      </v-list-item-action>
                    </v-list-item>
                  </draggable>
                </v-list>
              </v-card>
            </v-col>

            <!-- Mapping Visualization -->
            <v-col cols="12" md="4" class="d-flex flex-column">
              <h4 class="mb-3 d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-arrow-left-right</v-icon>
                Column Mappings ({{ Object.keys(columnMappings).length }})
                <v-chip v-if="getMappedCriticalFieldsCount() > 0" x-small color="success" class="ml-2">
                  {{ getMappedCriticalFieldsCount() }} Critical
                </v-chip>
                <v-chip v-if="getMappedRequiredFieldsCount() > 0" x-small color="success" class="ml-2">
                  {{ getMappedRequiredFieldsCount() }} Required
                </v-chip>
              </h4>
              <v-card outlined class="flex-grow-1 overflow-auto">
                <v-list v-if="Object.keys(columnMappings).length > 0" dense class="pt-0">
                  <v-list-item
                    v-for="(avaField, excelCol) in columnMappings"
                    :key="excelCol"
                    class="mapping-item">
                    <v-list-item-avatar size="32">
                      <v-icon color="success" size="16">mdi-arrow-right</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        <span class="font-weight-medium">{{ excelCol }}</span>
                        →
                        <span class="text-primary">{{ getFieldLabel(avaField) }}</span>
                        <v-chip v-if="isFieldCritical(avaField)" x-small color="error" class="ml-2">
                          CRITICAL
                        </v-chip>
                        <v-chip v-else-if="isFieldRequired(avaField)" x-small color="warning" class="ml-2">
                          Required
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ getColumnPreview(excelCol) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn x-small icon color="error" @click="removeMapping(excelCol)">
                        <v-icon size="14">mdi-close</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
                <div v-else class="pa-4 text-center text--secondary">
                  <v-icon large class="mb-2">mdi-arrow-left-right</v-icon>
                  <p>Drag Excel columns to AVA fields to create mappings</p>
                </div>
              </v-card>
            </v-col>

            <!-- AVA Fields -->
            <v-col cols="12" md="4" class="d-flex flex-column">
              <h4 class="mb-3 d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-cog</v-icon>
                AVA Fields ({{ sortedAvailableFields.length }})
                <v-chip v-if="getCriticalFieldsCount() > 0" x-small color="error" class="ml-2">
                  {{ getCriticalFieldsCount() }} Critical
                </v-chip>
                <v-chip v-if="getRequiredFieldsCount() > 0" x-small color="warning" class="ml-2">
                  {{ getRequiredFieldsCount() }} Required
                </v-chip>
                <v-chip v-if="getSpecialFieldsCount() > 0" x-small color="info" class="ml-2">
                  {{ getSpecialFieldsCount() }} Special
                </v-chip>
              </h4>
              <v-card outlined class="flex-grow-1 overflow-auto">
                <v-list dense class="pt-0">
                  <!-- Critical Fields Section -->
                  <v-list-item v-if="getCriticalFieldsCount() > 0" class="section-header critical-section">
                    <v-list-item-content>
                      <v-list-item-title class="text-caption font-weight-bold text-error">
                        <v-icon left small color="error">mdi-alert-circle</v-icon>
                        CRITICAL FIELDS
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item
                    v-for="field in getCriticalFields()"
                    :key="field.key"
                    class="column-item ava-field critical-field"
                    :class="{
                      mapped: isFieldMapped(field.key),
                    }">
                    <v-list-item-avatar size="32">
                      <v-icon color="error" size="16">mdi-alert-circle</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ field.label }}
                        <v-chip x-small color="error" class="ml-2">
                          CRITICAL
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ getMappedExcelColumn(field.key) || 'Not mapped' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <!-- Required Fields Section -->
                  <v-list-item v-if="getRequiredFieldsCount() > 0" class="section-header required-section">
                    <v-list-item-content>
                      <v-list-item-title class="text-caption font-weight-bold text-warning">
                        <v-icon left small color="warning">mdi-asterisk</v-icon>
                        REQUIRED FIELDS
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item
                    v-for="field in getRequiredFields()"
                    :key="field.key"
                    class="column-item ava-field required-field"
                    :class="{
                      mapped: isFieldMapped(field.key),
                    }">
                    <v-list-item-avatar size="32">
                      <v-icon color="warning" size="16">mdi-asterisk</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ field.label }}
                        <v-chip x-small color="warning" class="ml-2">
                          Required
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ getMappedExcelColumn(field.key) || 'Not mapped' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <!-- Special Fields Section -->
                  <v-list-item v-if="getSpecialFieldsCount() > 0" class="section-header special-section">
                    <v-list-item-content>
                      <v-list-item-title class="text-caption font-weight-bold text-info">
                        <v-icon left small color="info">mdi-star</v-icon>
                        SPECIAL FIELDS
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item
                    v-for="field in getSpecialFields()"
                    :key="field.key"
                    class="column-item ava-field special-field"
                    :class="{
                      mapped: isFieldMapped(field.key),
                    }">
                    <v-list-item-avatar size="32">
                      <v-icon color="info" size="16">mdi-star</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ field.label }}
                        <v-chip x-small color="info" class="ml-2">
                          Special
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ getMappedExcelColumn(field.key) || 'Not mapped' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <!-- Other Fields Section -->
                  <v-list-item v-if="getOtherFieldsCount() > 0" class="section-header other-section">
                    <v-list-item-content>
                      <v-list-item-title class="text-caption font-weight-bold text--secondary">
                        <v-icon left small color="grey">mdi-circle-outline</v-icon>
                        OTHER FIELDS
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item
                    v-for="field in getOtherFields()"
                    :key="field.key"
                    class="column-item ava-field"
                    :class="{
                      mapped: isFieldMapped(field.key),
                      special: field.special,
                    }">
                    <v-list-item-avatar size="32">
                      <v-icon :color="getFieldIconColor(field)" size="16">
                        {{ getFieldIcon(field) }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        {{ field.label }}
                        <v-chip v-if="field.special" x-small color="info" class="ml-2">
                          Special
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ getMappedExcelColumn(field.key) || 'Not mapped' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>


      <!-- Actions -->
      <v-card-actions class="pa-4 bg1">
        <v-btn @click="closeDialog" :disabled="isProcessing"> Cancel </v-btn>
        <v-btn
          color="info"
          @click="showPreview"
          :loading="isLoadingPreview"
          :disabled="Object.keys(columnMappings).length === 0">
          <v-icon left>mdi-eye</v-icon>
          Preview Data
          <v-chip v-if="facilityFilterStatus && facilityFilterStatus.isActive" x-small color="success" class="ml-2">
            Filtered
          </v-chip>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="submitMapping"
          :loading="isProcessing"
          :disabled="!canSubmit">
          <v-icon left>mdi-upload</v-icon>
          Process Data
          <v-icon 
            v-if="!canSubmit" 
            right 
            small 
            :color="processDataDisabledReasons.length > 0 && processDataDisabledReasons[0].type === 'error' ? 'error' : 'warning'">
            {{ processDataDisabledReasons.length > 0 && processDataDisabledReasons[0].icon || 'mdi-alert' }}
          </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Separate Preview Dialog -->
    <DataPreviewDialog
      v-model="showPreviewDialog"
      :preview-data="previewData"
      :column-mappings="columnMappings"
      :available-fields="availableFields"
      :loading="isLoadingPreview"
      :total-rows="uploadData.totalRows || 0"
      @refresh-preview="getPreview"
      @proceed-with-mapping="onPreviewProceed"
      @load-more-data="loadMorePreviewData"
      @save-edits="onPreviewDataSaved"
      @update:previewData="onPreviewDataSaved" />
  </v-dialog>
</template>

<script>
  import draggable from 'vuedraggable';
  import flexibleExcelService from '../services/flexibleExcel.service';
  import { detectFacilityColumn, validateRequiredMappings } from '../utils/excelParser';
  import forgePropertyMapper from '../utils/forgePropertyMapper';
  import DataPreviewDialog from './DataPreviewDialog.vue';
  import PropertySelector from './PropertySelector.vue';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'ColumnMappingDialog',
    components: {
      draggable,
      DataPreviewDialog,
      PropertySelector,
    },
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      uploadData: {
        type: Object,
        default: () => ({
          uploadId: null,
          fileName: '',
          detectedColumns: [],
          sampleData: {},
          facilitiesDetected: [],
          facilityColumn: null,
        }),
      },
      tagType: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        dialogVisible: false,
        columnMappings: {},
        excelColumns: [],
        availableFields: [],
        isDragging: false,
        previewData: [],
        validationResults: null,
        isLoadingPreview: false,
        isProcessing: false,
        facilitiesDetected: [],
        facilityColumn: null,
        showPreviewDialog: false,
        currentPreviewLimit: 1000,
        hasEditedPreviewData: false,
      };
    },
    computed: {
      facilityColumnOptions() {
        return this.excelColumns.map((col) => ({
          text: col.name,
          value: col.name,
        }));
      },
      canSubmit() {
        const hasTagNoMapping = Object.values(this.columnMappings).includes('name');
        return (
          Object.keys(this.columnMappings).length > 0 &&
          hasTagNoMapping &&
          this.validationResults?.isValid !== false &&
          !this.isProcessing
        );
      },

      hasTagNoMapping() {
        return Object.values(this.columnMappings).includes('name');
      },

      // Get detailed reasons why Process Data button is disabled
      processDataDisabledReasons() {
        const reasons = [];
        
        // Check if no mappings exist
        if (Object.keys(this.columnMappings).length === 0) {
          reasons.push({
            type: 'warning',
            icon: 'mdi-arrow-right',
            message: 'No column mappings created yet',
            solution: 'Drag Excel columns to AVA fields or use the PropertySelector to create mappings'
          });
        }
        
        // Check if Tag No. is not mapped
        if (Object.keys(this.columnMappings).length > 0 && !this.hasTagNoMapping) {
          reasons.push({
            type: 'error',
            icon: 'mdi-alert',
            message: 'Tag No. field is required but not mapped',
            solution: 'Map a column containing tag numbers to the "Tag No." field'
          });
        }
        
        // Check validation errors
        if (this.validationResults?.isValid === false && this.validationResults?.errors?.length > 0) {
          reasons.push({
            type: 'error',
            icon: 'mdi-alert-circle',
            message: `Validation errors: ${this.validationResults.errors.slice(0, 2).join(', ')}`,
            solution: 'Fix the validation errors shown above'
          });
        }
        
        // Check if currently processing
        if (this.isProcessing) {
          reasons.push({
            type: 'info',
            icon: 'mdi-loading',
            message: 'Data is currently being processed',
            solution: 'Please wait for the current process to complete'
          });
        }
        
        return reasons;
      },

      // Check if we should show the disabled alert
      shouldShowDisabledAlert() {
        return !this.canSubmit && this.processDataDisabledReasons.length > 0;
      },

      // Get current facility from store
      currentFacility() {
        return this.$store.getters['FACILITY/GET_FACILITY'];
      },

      // Filter Excel columns by current facility if facility column is selected
      filteredExcelColumns() {
        if (!this.facilityColumn || !this.currentFacility) {
          return this.excelColumns;
        }

        // Filter columns based on facility matching
        const facilityName = this.currentFacility.name;
        return this.excelColumns.filter(column => {
          // If this column is mapped to facility, check if it matches current facility
          if (this.columnMappings[column.name] === 'facilityName') {
            return true; // Always show facility column
          }
          
          // For other columns, check if they have facility-related data
          const sampleData = this.getColumnPreview(column.name);
          if (sampleData && sampleData.toLowerCase().includes(facilityName.toLowerCase())) {
            return true;
          }
          
          return true; // Show all columns for now, filtering will happen in data processing
        });
      },

      // Sort AVA fields to show critical and required fields first
      sortedAvailableFields() {
        return [...this.availableFields].sort((a, b) => {
          // Critical fields first
          if (a.critical && !b.critical) return -1;
          if (!a.critical && b.critical) return 1;
          
          // Required fields second
          if (a.required && !b.required) return -1;
          if (!a.required && b.required) return 1;
          
          // Then by label alphabetically
          return a.label.localeCompare(b.label);
        });
      },

      // Get facility filter status
      facilityFilterStatus() {
        if (!this.currentFacility) {
          return null;
        }
        
        const facilityName = this.currentFacility.name;
        const mappedFacilityColumn = Object.keys(this.columnMappings).find(
          col => this.columnMappings[col] === 'facilityName'
        );
        
        // Only return status if we have both a facility column selected AND a mapping exists
        if (!this.facilityColumn || !mappedFacilityColumn || this.facilityColumn !== mappedFacilityColumn) {
          return null;
        }
        
        return {
          isActive: true,
          facilityName,
          mappedColumn: mappedFacilityColumn,
          totalColumns: this.excelColumns.length,
          filteredColumns: this.filteredExcelColumns.length
        };
      }
    },
    watch: {
      value: {
        immediate: true,
        handler(newVal) {
          this.dialogVisible = newVal;
          if (newVal) {
            this.initializeDialog();
          }
        },
      },
      dialogVisible(newVal) {
        this.$emit('input', newVal);
      },
      columnMappings: {
        deep: true,
        handler() {
          this.validateMappings();
          
          // Check if facility mapping was removed and clear facility column if needed
          const hasFacilityMapping = Object.values(this.columnMappings).includes('facilityName');
          if (!hasFacilityMapping && this.facilityColumn) {
            // Clear facility column when facility mapping is removed
            this.facilityColumn = null;
            this.facilitiesDetected = [];
            
            // Reset preview data since facility filtering has changed
            this.hasEditedPreviewData = false;
            this.previewData = [];

            log('Facility mapping removed - cleared facility column selection');
          }
        },
      },
    },
    methods: {
      async initializeDialog() {
        try {
          // Check if we have valid upload data
          if (!this.uploadData || !this.uploadData.detectedColumns) {
            warn('No upload data available for mapping dialog');
            return;
          }

          // Initialize Excel columns from upload data
          this.excelColumns = this.uploadData.detectedColumns.map((name) => ({
            name,
            type: 'string', // Could be enhanced with type detection
          }));

          // Get available AVA fields for this tag type using forge property mapper
          const forgeProperties = forgePropertyMapper.getAvailableProperties(this.tagType);
          this.availableFields = forgeProperties.map((prop) => ({
            key: prop.property,
            label: prop.title,
            required: this.isRequiredField(prop.property, prop.title),
            critical: this.isCriticalField(prop.property),
            special: this.isSpecialField(prop.property),
            type: 'string',
          }));


          // Apply suggested mappings using forge property mapper
          const suggestions = forgePropertyMapper.createPropertyMapping(
            this.uploadData.detectedColumns,
            this.tagType,
          );
          this.columnMappings = { ...suggestions };

          // Detect facility column and auto-map it
          const detectedFacilityCol = detectFacilityColumn(this.uploadData.detectedColumns);
          if (detectedFacilityCol && this.uploadData.facilitiesDetected) {
            this.facilityColumn = detectedFacilityCol;
            this.facilitiesDetected = this.uploadData.facilitiesDetected;
            
            // Automatically create mapping for detected facility column
            // This is done after forge mapping to ensure it doesn't get overwritten
            this.columnMappings[detectedFacilityCol] = 'facilityName';
            
            log(`Auto-detected facility column: "${detectedFacilityCol}" mapped to facilityName`);
          }

          // Initial validation
          this.validateMappings();
        } catch (error) {
          error('Error initializing dialog:', error);
          this.$dialog.notify.error('Failed to initialize mapping dialog');
        }
      },

      isMapped(columnName) {
        return columnName in this.columnMappings;
      },

      isFieldMapped(fieldKey) {
        return Object.values(this.columnMappings).includes(fieldKey);
      },

      removeMapping(columnName) {
        delete this.columnMappings[columnName];
        this.columnMappings = { ...this.columnMappings };
      },

      onDragStart() {
        this.isDragging = true;
      },

      onDragEnd() {
        this.isDragging = false;
      },

      onFieldDrop(event) {
        // Handle drop of Excel column onto AVA field
        const excelColumn = event.added?.element;
        const targetIndex = event.added?.newIndex;

        if (excelColumn && targetIndex !== undefined) {
          const targetField = this.availableFields[targetIndex];
          this.columnMappings[excelColumn.name] = targetField.key;
          this.columnMappings = { ...this.columnMappings };
        }
      },

      validateMappings() {
        // Use forge property mapper validation
        const forgeValidation = forgePropertyMapper.validatePropertyMapping(
          this.columnMappings,
          this.tagType,
        );

        // Combine with existing validation for required fields
        const requiredValidation = validateRequiredMappings(this.columnMappings, this.tagType);

        // Merge validation results
        this.validationResults = {
          isValid: forgeValidation.isValid && requiredValidation.isValid,
          errors: [...(forgeValidation.errors || []), ...(requiredValidation.errors || [])],
          warnings: [...(forgeValidation.warnings || []), ...(requiredValidation.warnings || [])],
          unmappedHeaders: forgeValidation.unmappedHeaders || [],
          invalidMappings: forgeValidation.invalidMappings || [],
        };
      },

      getFieldLabel(fieldKey) {
        const field = this.availableFields.find((f) => f.key === fieldKey);
        return field ? field.label : fieldKey;
      },

      getFieldIcon(field) {
        if (field.required) return 'mdi-asterisk';
        if (field.special) return 'mdi-star';
        if (this.isFieldMapped(field.key)) return 'mdi-check-circle';
        return 'mdi-circle-outline';
      },

      getFieldIconColor(field) {
        if (field.critical) return 'error';
        if (field.required) return 'warning';
        if (field.special) return 'info';
        if (this.isFieldMapped(field.key)) return 'success';
        return 'grey';
      },

      getMappedExcelColumn(fieldKey) {
        for (const [excelCol, avaField] of Object.entries(this.columnMappings)) {
          if (avaField === fieldKey) return excelCol;
        }
        return null;
      },

      getColumnPreview(columnName) {
        // Return sample data for preview (would come from upload data)
        if (
          this.uploadData &&
          this.uploadData.sampleData &&
          this.uploadData.sampleData[columnName]
        ) {
          const sampleValues = this.uploadData.sampleData[columnName];
          if (Array.isArray(sampleValues) && sampleValues.length > 0) {
            return sampleValues.slice(0, 3).join(', ');
          }
        }
        return 'No preview available';
      },

      showPreview() {
        if (Object.keys(this.columnMappings).length === 0) {
          this.$dialog.notify.warning('Please map at least one column first');
          return;
        }

        // Check if Tag No. (name field) is mapped
        const hasTagNoMapping = Object.values(this.columnMappings).includes('name');
        if (!hasTagNoMapping) {
          this.$dialog.notify.error(
            'Tag No. mapping is required before preview. Please map a column to "Tag No." field.',
          );
          return;
        }

        // If we have edited data, show the dialog directly without refreshing
        if (this.hasEditedPreviewData && this.previewData.length > 0) {
          this.showPreviewDialog = true;
        } else {
          // Load preview data and show dialog
          this.getPreview().then(() => {
            this.showPreviewDialog = true;
          });
        }
      },

      async getPreview() {
        if (!this.uploadData || !this.uploadData.uploadId) {
          this.$dialog.notify.error('No upload session available for preview');
          return;
        }

        this.isLoadingPreview = true;
        try {
          // Add facility filtering options
          const options = {
            facilityColumn: this.facilityColumn,
            filterByFacility: this.currentFacility?.name
          };

          const response = await flexibleExcelService.getPreviewData(
            this.uploadData.uploadId,
            this.$store.state.assetId,
            this.columnMappings,
            1000, // Load 1000 rows by default for Vuetify table to handle
            options
          );
          this.previewData = response.data || [];
          this.hasEditedPreviewData = false; // Reset edit flag when fresh data is loaded
        } catch (error) {
          error('Error getting preview:', error);
          this.$dialog.notify.error('Failed to load preview data');
        } finally {
          this.isLoadingPreview = false;
        }
      },

      onPreviewProceed() {
        // User confirmed the preview looks good
        this.$dialog.notify.success('Preview confirmed! Ready to process data.');
      },

      onPreviewDataSaved(updatedData) {
        // Update the preview data with saved edits
        this.previewData = updatedData;
        this.hasEditedPreviewData = true;
      },

      async loadMorePreviewData() {
        // Load more data for preview (increase limit)
        this.currentPreviewLimit = Math.min(this.currentPreviewLimit * 2, 10000);
        await this.getPreview();
        this.$dialog.notify.success(
          `Loaded more data. Now showing ${this.previewData.length} rows.`,
        );
      },

      onFacilityColumnChange() {
        // Handle facility column selection/deselection
        
        // First, remove any existing 'facilityName' mappings
        const existingFacilityMapping = Object.keys(this.columnMappings).find(
          col => this.columnMappings[col] === 'facilityName'
        );
        if (existingFacilityMapping) {
          delete this.columnMappings[existingFacilityMapping];
        }
        
        // If a facility column is selected, add the new mapping
        if (this.facilityColumn) {
          this.columnMappings[this.facilityColumn] = 'facilityName';
        }
        
        // Trigger reactivity
        this.columnMappings = { ...this.columnMappings };
        
        // Reset preview data since facility filtering may have changed
        this.hasEditedPreviewData = false;
        this.previewData = [];
      },

      // Helper methods for field categorization
      getCriticalFields() {
        return this.availableFields.filter(field => field.critical);
      },

      getRequiredFields() {
        return this.availableFields.filter(field => field.required && !field.critical);
      },

      getSpecialFields() {
        return this.availableFields.filter(field => field.special && !field.critical && !field.required);
      },

      getOtherFields() {
        return this.availableFields.filter(field => !field.required && !field.critical && !field.special);
      },

      getCriticalFieldsCount() {
        return this.getCriticalFields().length;
      },

      getRequiredFieldsCount() {
        return this.getRequiredFields().length;
      },

      getSpecialFieldsCount() {
        return this.getSpecialFields().length;
      },

      getOtherFieldsCount() {
        return this.getOtherFields().length;
      },

      // Count mapped fields by type
      getMappedCriticalFieldsCount() {
        return this.getCriticalFields().filter(field => this.isFieldMapped(field.key)).length;
      },

      getMappedRequiredFieldsCount() {
        return this.getRequiredFields().filter(field => this.isFieldMapped(field.key)).length;
      },

      // Helper methods for checking field types
      isFieldCritical(fieldKey) {
        const field = this.availableFields.find(f => f.key === fieldKey);
        return field ? field.critical : false;
      },

      isFieldRequired(fieldKey) {
        const field = this.availableFields.find(f => f.key === fieldKey);
        return field ? field.required : false;
      },

      // Field type determination methods
      isRequiredField(property, title) {
        // Tag name is always required
        if (property === 'name') return true;
        
        // Fields with asterisk in title are required
        if (title && title.includes('*')) return true;
        
        // Additional required fields based on tag type
        const requiredFields = {
          'ForgeEquipment': ['name'],
          'ForgePipeline': ['name'],
          'ForgeStructural': ['name'],
          'ForgeInspection': ['name'],
          'ForgeAnomaly': ['name'],
          'ForgeAnnotation': ['name'],
          'ForgeScaffold': ['name']
        };
        
        return requiredFields[this.tagType]?.includes(property) || false;
      },

      isCriticalField(property) {
        // Tag name is always critical
        if (property === 'name') return true;
        
        // Critical fields that affect data integrity
        const criticalFields = {
          'ForgeEquipment': ['name', 'equipmentDescription', 'manufacturer'],
          'ForgePipeline': ['name', 'lineNo', 'from', 'to'],
          'ForgeStructural': ['name', 'description', 'type'],
          'ForgeInspection': ['name', 'tagDescription', 'inspectionDate'],
          'ForgeAnomaly': ['name', 'description', 'anomalyType'],
          'ForgeAnnotation': ['name', 'description'],
          'ForgeScaffold': ['name', 'description']
        };
        
        return criticalFields[this.tagType]?.includes(property) || false;
      },

      isSpecialField(property) {
        // Special fields that have unique handling and are NOT in standard Excel mappings
        // These are system-added fields that provide special functionality
        const specialFields = {
          'Equipment': ['facilityName'],
          'ForgeEquipment': ['facilityName'],
          'Pipeline': ['facilityName'], 
          'ForgePipeline': ['facilityName'],
          'Structural': ['facilityName'],
          'ForgeStructural': ['facilityName'],
          'Inspection': ['facilityName'],
          'ForgeInspection': ['facilityName'],
          'Anomaly': ['facilityName'],
          'ForgeAnomaly': ['facilityName'],
          'Annotation': ['facilityName'],
          'ForgeAnnotation': ['facilityName'],
          'Scaffold': ['facilityName'],
          'ForgeScaffold': ['facilityName']
        };
        
        return specialFields[this.tagType]?.includes(property) || false;
      },

      // Get exact matches for a column header
      getExactMatches(columnHeader) {
        const matches = forgePropertyMapper.getExactMatches([columnHeader], this.tagType);
        return matches;
      },

      // Handle property selection from PropertySelector component
      onPropertySelected(selection) {
        this.columnMappings[selection.columnHeader] = selection.property;
        this.columnMappings = { ...this.columnMappings };
      },

      async submitMapping() {
        if (!this.canSubmit) return;

        if (!this.uploadData || !this.uploadData.uploadId) {
          this.$dialog.notify.error('No upload session available for processing');
          return;
        }

        this.isProcessing = true;
        try {
          const mappingData = {
            mappings: this.columnMappings,
            facilityColumn: this.facilityColumn,
            type: this.tagType,
          };

          // If we have edited preview data, use it directly
          if (this.hasEditedPreviewData && this.previewData.length > 0) {
            await this.submitProcessedData(mappingData);
          } else {
            // For unedited data, first get preview data then process it
            if (this.previewData.length === 0) {
              await this.getPreview();
            }
            await this.submitProcessedData(mappingData);
          }

          this.closeDialog();
        } catch (error) {
          error('Error submitting mapping:', error);
          this.$dialog.notify.error(`Failed to process data: ${error.message}`);
        } finally {
          this.isProcessing = false;
        }
      },

      async submitProcessedData(mappingData) {
        // Create instances from edited preview data
        this.$emit('upload-progress', {
          stage: 'preparing',
          progress: 10,
          message: 'Preparing data instances...',
        });

        const processedRecords = this.createDataInstances();

        this.$emit('upload-progress', {
          stage: 'submitting',
          progress: 70,
          message: 'Submitting processed data...',
        });

        // Submit to server with forgeAssetId
        const response = await flexibleExcelService.submitProcessedData(this.$route.params.id, {
          type: mappingData.type,
          records: processedRecords,
          forgeAssetId: this.$route.params.id,
        });

        this.$emit('upload-progress', {
          stage: 'complete',
          progress: 100,
          message: 'Data processing complete!',
        });

        this.$dialog.notify.success('Data processed successfully');
        this.$emit('mapping-complete', response.data);
      },

      createDataInstances() {
        // Convert preview data to array of arrays with CellValueInfo objects
        const entitiesArray = [];

        this.previewData.forEach((row) => {
          // Skip rows without Tag No.
          const tagNoField = Object.keys(this.columnMappings).find(
            (col) => this.columnMappings[col] === 'name',
          );
          if (!tagNoField || !row[tagNoField] || String(row[tagNoField]).trim() === '') {
            return;
          }

          const entityCells = [];

          // Map each column according to user mappings
          Object.entries(this.columnMappings).forEach(([excelColumn, avaField]) => {
            const value = row[excelColumn];

            if (value !== null && value !== undefined && String(value).trim() !== '') {
              // Create CellValueInfo object
              const cellValueInfo = {
                Value: this.convertExcelValue(value),
                ColumnTitle: avaField,
              };
              entityCells.push(cellValueInfo);
            }
          });

          // Only include entities with valid Tag No.
          const tagNoCell = entityCells.find((cell) => cell.ColumnTitle === 'name');
          if (tagNoCell && String(tagNoCell.Value).trim() !== '') {
            entitiesArray.push({ cells: entityCells });
          }
        });

        return entitiesArray;
      },

      convertExcelValue(value) {
        // At this point, if Excel dates were properly converted during parsing,
        // they should already be in string format. Just ensure proper trimming.
        if (value !== null && value !== undefined) {
          return String(value).trim();
        }
        return '';
      },

      closeDialog() {
        this.dialogVisible = false;
        this.resetDialog();
      },

      resetDialog() {
        this.columnMappings = {};
        this.excelColumns = [];
        this.previewData = [];
        this.validationResults = null;
        this.facilityColumn = null;
        this.facilitiesDetected = [];
        this.showPreviewDialog = false;
      },
    },
  };
</script>

<style scoped>
  .column-mapping-dialog .v-dialog {
    overflow-y: auto;
  }

  .column-item {
    transition: all 0.3s ease;
    cursor: move;
  }

  .column-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .column-item.mapped {
    background-color: rgba(76, 175, 80, 0.1);
    border-left: 3px solid #4caf50;
  }

  .column-item.required {
    border-left: 3px solid #f44336;
  }

  .column-item.special {
    border-left: 3px solid #ff9800;
  }

  .column-item.dragging {
    opacity: 0.5;
  }

  .ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }

  .list-group {
    min-height: 200px;
  }

  .mapping-item {
    background-color: rgba(33, 150, 243, 0.05);
    margin: 2px 0;
    border-radius: 4px;
  }

  .excel-column {
    border-left: 3px solid #ff9800;
  }

  .ava-field {
    cursor: text;
    border-left: 3px solid #ff9000;
  }

  .section-header {
    background-color: rgba(0, 0, 0, 0.02);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    min-height: 40px;
  }

  .critical-section {
    background-color: rgba(244, 67, 54, 0.05);
    border-left: 3px solid #f44336;
  }

  .required-section {
    background-color: rgba(255, 152, 0, 0.05);
    border-left: 3px solid #ff9800;
  }

  .special-section {
    background-color: rgba(33, 150, 243, 0.05);
    border-left: 3px solid #2196f3;
  }

  .other-section {
    background-color: rgba(158, 158, 158, 0.05);
    border-left: 3px solid #9e9e9e;
  }

  .critical-field {
    border-left: 3px solid #f44336;
    background-color: rgba(244, 67, 54, 0.02);
  }

  .required-field {
    border-left: 3px solid #ff9800;
    background-color: rgba(255, 152, 0, 0.02);
  }

  .special-field {
    border-left: 3px solid #2196f3;
    background-color: rgba(33, 150, 243, 0.02);
  }

  .critical-field:hover {
    background-color: rgba(244, 67, 54, 0.05);
  }

  .required-field:hover {
    background-color: rgba(255, 152, 0, 0.05);
  }

  .special-field:hover {
    background-color: rgba(33, 150, 243, 0.05);
  }

  .bg-light {
    background-color: rgba(0, 0, 0, 0.02);
  }
</style>
